import { useMutation, useQuery } from "@tanstack/react-query";
import { Fragment, type ReactNode } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ApiError, apiGet } from "../lib/api";
import { cn } from "../lib/utils";
import { AgentPC } from "./agent-pc";
import { essaiBotSchema, etatSchema, type TwitchAccount, type TwitchEtat } from "./contract";
import { DIFFUSION_LINK, DIFFUSION_PATH, REGIE_NAV_LABEL } from "./diffusion-entree";
import { EssaiObs } from "./essai-obs";
import { LeverOppositionDiscordForm, LeverOppositionForm, OppositionForm } from "./opposition-form";
import {
  BROWSER_NOTE,
  ESSAI_BOT_FAILED,
  ESSAI_BOT_MESSAGES,
  eventLabel,
  eventSubText,
  formatDay,
  formatTime,
  linkErrorMessage,
  ROLES,
  RUNTIME_MESSAGES,
  refusedSubscriptionsText,
  returnMessage,
} from "./regie-texts";
import { CARTE, CadreReglages, LIEN, TITRE_CARTE } from "./reglages/cadre";
import { SignIn } from "./sign-in";

/**
 * La regie, version minimale : d'ici, Nicolas (le seul admin) autorise les deux comptes Twitch, la
 * chaine et le bot. Ce fichier t'appartient, comme tout `src/app/` ; la page sera redessinee plus
 * tard avec des maquettes, elle ne fait ici que l'indispensable.
 *
 * Ce que la page montre depend de la reponse de `GET /api/twitch/etat` (les routes de
 * `backend/src/app/twitch/routes.ts`, derriere la garde de l'admin) :
 * - 401 : le formulaire du lien magique, et aucune donnee ;
 * - 403 : une phrase et le meme formulaire (le lien d'une autre adresse remplace la session), sans
 *   aucune donnee ;
 * - Twitch non configure : le nom des variables qui manquent ;
 * - sinon, les deux comptes, la chaine d'abord, chacun avec son etat et un lien pour l'autoriser, puis
 *   la liaison : le travail de fond (une phrase par raison), EventSub, le dernier releve, le stream
 *   ouvert, les presents comptes, les dix derniers evenements, et le bouton « Essai du bot ».
 *
 * Sous les comptes et la liaison, la carte « Agent du PC » (`agent-pc.tsx`) dit si le programme du PC
 * de Nicolas est la, et liste ses taches en echec avec un bouton « Relancer » ; elle lit sa propre
 * route (`GET /api/diffusion/atelier`) et ne depend pas de Twitch. Puis le formulaire « Effacer un
 * viewer a sa demande » (`opposition-form.tsx`) tient l'opposition de la politique de confidentialite.
 * En tete de la page, sous le titre et dans tous ses etats (chargement, connexion, erreur, comptes), le lien
 * « Diffusion » mene a l'ecran de la diffusion (`diffusion.tsx`, `/regie/diffusion` : la semaine, le stock, le suivi
 * des envois, les reseaux et les reglages), qui ne depend ni de Twitch ni de la lecture ci-dessous ; c'est un
 * simple `<a>`, la page se monte sans routeur.
 *
 * Le bouton « Autoriser » est un lien de navigation, pas un appel `fetch` : la route repond par une
 * redirection vers Twitch, et Twitch rappelle le backend, qui revient ici avec `?twitch=<code>`.
 * Un lien magique en echec revient ici avec `?error=<code>`. La route lit ces deux codes, les retire
 * de l'adresse (`routes.tsx`) et les donne a la page, qui ne les confronte qu'a des listes fermees.
 */

/**
 * L'adresse de la regie : l'ecran En direct (`direct/en-direct.tsx`, PR E du lot Q1). Les retours de Twitch (`?twitch=`) et
 * des liens magiques en echec (`?error=`) y arrivent encore (le backend les y renvoie) et la route les passe a
 * `REGLAGES_TWITCH_PATH`.
 */
export const REGIE_PATH = "/regie";

/**
 * L'adresse de cette page, depuis la PR E du lot Q1 : les comptes Twitch, la liaison, l'agent du PC, l'essai d'OBS et
 * l'opposition. Le lien magique y ramene une personne qui s'y est connectee.
 */
export const REGLAGES_TWITCH_PATH = "/regie/reglages/twitch";

/** Le delai entre deux relectures de l'etat, page ouverte. */
export const REFRESH_MS = 8_000;

/** Le lien « Autoriser » a l'allure d'un bouton : `Button` rend un `<button>`, pas un lien. */
const LINK_BUTTON =
  "inline-flex h-10 w-fit items-center justify-center rounded-md bg-primary px-4 text-[20px] font-medium text-primary-foreground transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/** Des noms (droits Twitch, variables d'environnement) separes par des virgules. */
function NameList({ names }: { names: readonly string[] }) {
  return (
    <>
      {names.map((name, index) => (
        <Fragment key={name}>
          {index > 0 ? ", " : null}
          <code>{name}</code>
        </Fragment>
      ))}
    </>
  );
}

function AccountDetails({ account }: { account: TwitchAccount }) {
  const authorizedOn = formatDay(account.autoriseLe);
  // `valideLe` vide n'est pas un defaut : le travail de fond qui le remplit n'existe pas encore.
  const validatedOn = account.valideLe === null ? null : formatDay(account.valideLe);
  return (
    <>
      {authorizedOn ? <p className="text-muted-foreground">Autorisé le {authorizedOn}.</p> : null}
      {validatedOn ? (
        <p className="text-muted-foreground">Dernière vérification le {validatedOn}.</p>
      ) : null}
      {account.etat === "a_reautoriser" && account.erreur ? <p>Raison : {account.erreur}</p> : null}
      {account.scopesManquants.length > 0 ? (
        <p>
          Droits manquants : <NameList names={account.scopesManquants} />. Réautorise le compte pour
          les obtenir.
        </p>
      ) : null}
    </>
  );
}

function AccountCard({
  entry,
  account,
}: {
  entry: (typeof ROLES)[number];
  account: TwitchAccount | undefined;
}) {
  const state =
    account === undefined
      ? "pas encore autorisé"
      : account.etat === "ok"
        ? "autorisé"
        : "à réautoriser";
  // Un compte relie montre son vrai login (du texte, jamais interprete) ; un role pas encore
  // autorise dit le compte qu'on attend. Les logins que Twitch rend sont en minuscules, mais la
  // comparaison ne depend pas de la casse.
  const unexpected = account !== undefined && account.login.toLowerCase() !== entry.login;
  return (
    <Card className={CARTE}>
      <CardHeader>
        <CardTitle className={TITRE_CARTE}>{entry.label}</CardTitle>
        <p className="text-[20px] text-muted-foreground">
          {account === undefined
            ? `Compte attendu : ${entry.login}`
            : `Compte autorisé : ${account.login}`}
        </p>
        {unexpected ? (
          <p className="text-[20px] text-destructive">
            {`Ce n'est pas le compte attendu (${entry.login}). Réautorise avec le bon compte.`}
          </p>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-[20px]">
        <p>
          État :{" "}
          <strong className={cn(account?.etat === "a_reautoriser" && "text-destructive")}>
            {state}
          </strong>
        </p>
        {account ? <AccountDetails account={account} /> : null}
        <a className={LINK_BUTTON} href={`/api/twitch/autoriser/${entry.role}`} rel="nofollow">
          {account ? "Réautoriser" : "Autoriser"}
        </a>
      </CardContent>
    </Card>
  );
}

function NotConfigured({ missing }: { missing: readonly string[] }) {
  return (
    <Card className={CARTE}>
      <CardHeader>
        <CardTitle className={TITRE_CARTE}>Twitch n'est pas encore configuré</CardTitle>
      </CardHeader>
      {missing.length > 0 ? (
        <CardContent className="text-[20px]">
          <p>
            Il manque ces variables sur le serveur : <NameList names={missing} />.
          </p>
        </CardContent>
      ) : null}
    </Card>
  );
}

/** Une requete sans corps, comme `apiGet` mais en POST : le resultat est lu par le contrat. */
async function essaiBotRequest() {
  const response = await fetch("/api/twitch/essai-bot", {
    method: "POST",
    headers: { accept: "application/json" },
  });
  const payload: unknown = await response.json().catch(() => null);
  const result = essaiBotSchema.safeParse(payload);
  if (!response.ok || !result.success) {
    throw new ApiError("essai_bot_failed", "Essai du bot en echec", response.status);
  }
  return result.data;
}

/** Le bouton « Essai du bot » et la phrase de son resultat, ecrite par la page. */
function EssaiBot() {
  const essai = useMutation({ mutationFn: essaiBotRequest });
  const message = essai.isError
    ? { text: ESSAI_BOT_FAILED, tone: "problem" as const }
    : essai.isSuccess
      ? ESSAI_BOT_MESSAGES[essai.data.resultat]
      : null;
  return (
    <div className="flex flex-col items-start gap-2">
      <Button disabled={essai.isPending} onClick={() => essai.mutate()}>
        Essai du bot
      </Button>
      {essai.isPending ? (
        <p className="text-muted-foreground" role="status">
          Essai en cours...
        </p>
      ) : message ? (
        <p className={cn(message.tone === "problem" && "text-destructive")} role="status">
          {message.text}
        </p>
      ) : null}
    </div>
  );
}

/** Le travail de fond, le direct et le journal : ce que la connexion a Twitch fait en ce moment. */
function Liaison({ etat }: { etat: TwitchEtat }) {
  const { runtime, stream } = etat;
  const streamStart = stream === null ? null : formatTime(stream.debut);
  const lastReading = runtime?.dernierReleveLe == null ? null : formatTime(runtime.dernierReleveLe);
  const runtimeMessage = runtime === null ? null : RUNTIME_MESSAGES[runtime.raison];
  const refused = runtime === null ? null : refusedSubscriptionsText(runtime.abonnementsRefuses);
  return (
    <Card className={CARTE}>
      <CardHeader>
        <CardTitle className={TITRE_CARTE}>La liaison avec Twitch</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-[20px]">
        {runtimeMessage ? (
          <p className={cn(runtimeMessage.tone === "problem" && "text-destructive")}>
            {runtimeMessage.text}
          </p>
        ) : (
          <p className="text-muted-foreground">
            Le travail de fond n'existe pas dans ce processus.
          </p>
        )}
        {runtime ? (
          <>
            <p>{eventSubText(runtime.eventsub)}</p>
            {refused ? <p className="text-destructive">{refused}</p> : null}
            <p>
              {lastReading
                ? `Dernier relevé de présence : ${lastReading}.`
                : "Aucun relevé de présence pour le moment."}
            </p>
          </>
        ) : null}
        {etat.journalLisible ? (
          <>
            <p>
              {stream === null
                ? "Aucun direct en cours."
                : streamStart
                  ? `Direct en cours depuis ${streamStart}.`
                  : "Direct en cours."}
            </p>
            {stream === null ? null : (
              <p>
                Présents comptés : <strong>{etat.presents}</strong>
              </p>
            )}
            <Derniers derniers={etat.derniers} />
          </>
        ) : (
          <p className="text-destructive">
            Le journal n'a pas pu être lu. Réessaie dans un instant.
          </p>
        )}
        <EssaiBot />
      </CardContent>
    </Card>
  );
}

function Derniers({ derniers }: { derniers: TwitchEtat["derniers"] }) {
  if (derniers.length === 0) {
    return <p className="text-muted-foreground">Aucun événement dans le journal.</p>;
  }
  return (
    <table className="w-full text-left">
      <caption className="pb-1 text-left font-medium">Derniers événements</caption>
      <tbody>
        {derniers.map((event, index) => (
          // Pas d'identifiant dans le contrat : le rang suffit, la liste est relue en entier.
          // biome-ignore lint/suspicious/noArrayIndexKey: liste relue en entier, sans etat par ligne
          <tr key={index}>
            <td className="pr-3">{eventLabel(event.type)}</td>
            <td className="pr-3 text-muted-foreground">{formatTime(event.survenuLe) ?? ""}</td>
            <td>{event.nom ?? ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** La page quand l'admin est connecte : la phrase du retour de Twitch, puis les comptes. */
function Connected({ etat, twitch }: { etat: TwitchEtat; twitch: string | undefined }) {
  const message = returnMessage(twitch);
  return (
    <>
      {message ? (
        <p
          className={cn("text-[20px]", message.tone === "problem" && "text-destructive")}
          role="status"
        >
          {message.text}
        </p>
      ) : null}
      {etat.configure ? (
        <>
          <ul className="flex flex-col gap-4">
            {ROLES.map((entry) => (
              <li key={entry.role}>
                <AccountCard
                  account={etat.comptes.find((account) => account.role === entry.role)}
                  entry={entry}
                />
              </li>
            ))}
          </ul>
          <Liaison etat={etat} />
        </>
      ) : (
        <NotConfigured missing={etat.manquantes} />
      )}
      <AgentPC />
      <EssaiObs />
      <OppositionForm />
      <LeverOppositionForm />
      <LeverOppositionDiscordForm />
      <p className="text-[20px] text-muted-foreground">{BROWSER_NOTE}</p>
    </>
  );
}

/**
 * `twitch` est le code lu dans l'adresse (`/regie?twitch=<code>`), tel que la route l'a trouve :
 * seul un code de la liste fermee du contrat affiche une phrase, a un admin connecte.
 * `linkError` est le code `?error=<code>` d'un lien magique en echec : seul un code de la liste
 * fermee de `regie-texts.ts` affiche une phrase, au-dessus d'un formulaire de connexion.
 */
export function RegiePage({
  twitch,
  linkError,
}: {
  twitch?: string | undefined;
  linkError?: string | undefined;
}) {
  const etat = useQuery({
    queryKey: ["twitch", "etat"],
    queryFn: () => apiGet("/api/twitch/etat", etatSchema),
    // Un 401 ou un 403 n'est pas une panne passagere : on ne le redemande pas. Et pas de relecture
    // au retour sur l'onglet ni au retour du reseau : une lecture qui reprend repasse par
    // « Chargement... » et ferait disparaitre le formulaire de connexion, avec l'adresse que
    // Nicolas vient d'y taper.
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Rien en memoire quand la page se demonte : le jour ou la page saura se deconnecter, la
    // personne suivante du meme onglet ne doit pas voir, meme un instant, les comptes de l'admin.
    gcTime: 0,
    // L'etat de la liaison change pendant que la page est ouverte (un direct commence, la connexion
    // se coupe) : la page le relit toute seule. Une relecture avec des donnees en memoire ne repasse
    // pas par « Chargement... ». Sur une erreur (401, 403, 500), plus de relecture : elle ferait
    // disparaitre le formulaire de connexion et l'adresse qu'on y tape.
    refetchInterval: (query) => (query.state.status === "error" ? false : REFRESH_MS),
  });

  let body: ReactNode;
  if (etat.isPending) {
    body = (
      <p className="text-[20px] text-muted-foreground" role="status">
        Chargement...
      </p>
    );
  } else if (etat.isError) {
    const status = etat.error instanceof ApiError ? etat.error.status : null;
    const notice = linkErrorMessage(linkError);
    if (status === 401) {
      body = <SignIn callbackURL={REGLAGES_TWITCH_PATH} notice={notice} />;
    } else if (status === 403) {
      // Pas d'impasse : le lien magique d'une autre adresse remplace la session de ce compte.
      body = (
        <>
          <p className="text-[20px]">Ce compte n'a pas accès à la régie.</p>
          <SignIn callbackURL={REGLAGES_TWITCH_PATH} notice={notice} />
        </>
      );
    } else {
      body = (
        <div className="flex flex-col items-start gap-3">
          <p className="text-[20px] text-destructive" role="alert">
            L'état des comptes n'a pas pu être lu. Réessaie dans un instant.
          </p>
          <Button onClick={() => void etat.refetch()}>Réessayer</Button>
        </div>
      );
    }
  } else {
    body = <Connected etat={etat.data} twitch={twitch} />;
  }

  return (
    <CadreReglages titre="La régie">
      {/* Dans tous les etats de la page : l'ecran de la diffusion ne depend pas de Twitch, ni de cette lecture. */}
      <nav aria-label={REGIE_NAV_LABEL} className="text-[20px]">
        <a className={LIEN} href={DIFFUSION_PATH}>
          {DIFFUSION_LINK}
        </a>
      </nav>
      {/* Jersey ne descend pas sous 18 px : les boutons du template (text-sm) y sont remontes. */}
      <div className="contents [&_button]:text-[18px]">{body}</div>
    </CadreReglages>
  );
}
